<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { required, maxLength } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ProductPoolDO = Modules.CampaignPlanner.Data.ProductPoolDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

const { t } = useI18n();

const props = defineProps<{
	poolToEdit: ProductPoolDO | null;
}>();

const emit = defineEmits<{
	"update-list": [];
}>();

const isDialogVisible = defineModel<boolean>();

const isNew = computed(() => !props.poolToEdit?.uuid);

type LocalPool = {
	identifier: string;
	is_single_location_for_spread_algorithm: boolean;
	pqa_allowed_amounts_screens: number[];
	screen_ids: number[];
};

const localPool = ref<LocalPool>({
	identifier: "",
	is_single_location_for_spread_algorithm: true,
	pqa_allowed_amounts_screens: [0, 1],
	screen_ids: [],
});

const allScreens = ref<ScreenDO[]>([]);
const formRef = ref();

const screenById = computed(() => new Map(allScreens.value.map((s) => [s.id, s])));

const matchedScreenIdSet = computed(
	() => new Set(localPool.value.screen_ids.filter((id) => screenById.value.has(id)))
);

const rawToId = (raw: unknown): number =>
	typeof raw === "object" && raw !== null ? (raw as ScreenDO).id : (raw as number);

const selectionLabel = (raw: unknown): string => {
	const id = rawToId(raw);
	return screenById.value.get(id)?.reference_id ?? String(id);
};

const pqaOptions = computed(() =>
	Array.from({ length: localPool.value.screen_ids.length + 1 }, (_, i) => i)
);

const identifierRule = (v: string) =>
	/^[a-zA-Z0-9_]+$/.test(v) || "Only letters, digits and underscores allowed";

watch(
	() => props.poolToEdit,
	(pool) => {
		if (pool?.uuid) {
			localPool.value = {
				identifier: pool.identifier,
				is_single_location_for_spread_algorithm: pool.is_single_location_for_spread_algorithm,
				pqa_allowed_amounts_screens: pool.pqa_allowed_amounts_screens ?? [],
				screen_ids: (pool.screens as ScreenDO[] | undefined)?.map((s) => s.id) ?? [],
			};
		} else {
			localPool.value = {
				identifier: "",
				is_single_location_for_spread_algorithm: true,
				pqa_allowed_amounts_screens: [0, 1],
				screen_ids: [],
			};
		}
	},
	{ immediate: true }
);

onMounted(async () => {
	const response = await useApiClient("campaignPlanner").getScreens({
		page: 1,
		per_page: 1000,
		"filter[active]": "true",
		"filter[is_hidden]": "false",
	});
	if (response.isOk()) {
		allScreens.value = response.value.data;
	}
});

const save = async () => {
	const { valid } = await formRef.value.validate();
	if (!valid) return;

	const response = await toastApiResult(
		isNew.value
			? useApiClient("campaignPlanner").createProductPool(localPool.value)
			: useApiClient("campaignPlanner").updateProductPool(props.poolToEdit!.uuid, localPool.value)
	);

	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("update-list");
	}
};

const screenItemTitle = (item: ScreenDO) =>
	[item.reference_id, item.name].filter(Boolean).join(" - ");
</script>

<template>
	<v-dialog v-model="isDialogVisible" max-width="640" persistent>
		<v-card>
			<v-toolbar color="lightprimary" elevation="0" flat>
				<template #prepend>
					<v-icon class="ml-3">fa: fa-regular fa-layer-group</v-icon>
				</template>
				<v-toolbar-title>{{ isNew ? "Add group" : "Edit group" }}</v-toolbar-title>
				<template #append>
					<v-btn icon variant="text" @click="isDialogVisible = false">
						<v-icon>fa: fa-regular fa-xmark</v-icon>
					</v-btn>
				</template>
			</v-toolbar>

			<v-divider />

			<v-form ref="formRef">
				<v-card-text class="pa-6">
					<v-row>
						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">Identifier</div>
							<div class="text-body-2 text-medium-emphasis mb-2">
								Unique name used internally. Only letters, digits and underscores.
							</div>
							<v-text-field
								v-model="localPool.identifier"
								variant="filled"
								clearable
								hide-details="auto"
								:rules="[required(t), maxLength(t, 255), identifierRule]"
							/>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">
								Single location for spread algorithm
							</div>
							<div class="text-body-2 text-medium-emphasis mb-2">
								If one screen in this group is matched by the spread algorithm,
								all screens in the group are added.
							</div>
							<div class="d-flex align-center gap-2">
								<v-checkbox
									v-model="localPool.is_single_location_for_spread_algorithm"
									hide-details
									color="primary"
								/>
								<span class="text-body-2">Treat as single location</span>
							</div>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">Screens</div>
							<div class="text-body-2 text-medium-emphasis mb-2">
								Active, visible screens available for this group.
							</div>
							<v-autocomplete
								v-model="localPool.screen_ids"
								variant="filled"
								:items="allScreens"
								:item-title="screenItemTitle"
								item-value="id"
								multiple
								clearable
								hide-details
							>
								<template v-slot:selection="{ item, index }">
									<v-chip
										v-if="index < 3 && matchedScreenIdSet.has(rawToId(item.raw))"
										size="small"
									>
										{{ selectionLabel(item.raw) }}
									</v-chip>
									<span
										v-if="index === 3"
										class="text-caption text-medium-emphasis align-self-center ml-1"
									>
										(+{{ localPool.screen_ids.length - 3 }} others)
									</span>
								</template>
							</v-autocomplete>
						</v-col>

						<v-col cols="12">
							<v-divider class="my-2" />
						</v-col>

						<v-col cols="12">
							<div class="text-subtitle-1 font-weight-semibold mb-1">
								PQA allowed screen counts
							</div>
							<div class="text-body-2 text-medium-emphasis mb-2">
								Allowed number of screens for the PQA algorithm. Options update
								based on the number of selected screens.
							</div>
							<v-select
								v-model="localPool.pqa_allowed_amounts_screens"
								:items="pqaOptions"
								variant="filled"
								multiple
								chips
								closable-chips
								hide-details
							/>
						</v-col>
					</v-row>
				</v-card-text>
			</v-form>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" @click="isDialogVisible = false">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" @click="save">Save</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
